import React, { useState, useEffect } from 'react';
import { 
  MessageSquare, Mail, Building, Save, Loader2, 
  Phone, MapPin, Palette, RefreshCw, Eye
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const MessagingSettings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('company');
  const [previewTemplate, setPreviewTemplate] = useState(null);
  
  const [config, setConfig] = useState({
    company_name: '',
    company_phone: '',
    service_area: '',
    sms: {
      enabled: true,
      twilio_phone_number: '',
      sender_name: '',
      intro_message_template: '',
      auto_respond_enabled: true,
      estimate_auto_send: true,
      followup_delay_minutes: 10,
      require_sms_consent: false
    },
    email: {
      enabled: true,
      sender_name: '',
      sender_email: '',
      reply_to_email: '',
      primary_color: '#F97316',
      send_estimate_email: true,
      send_followup_emails: true,
      send_review_requests: true,
      send_referral_requests: true
    }
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/messaging-settings/`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setConfig(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load messaging settings');
    } finally {
      setLoading(false);
    }
  };

  const saveCompanySettings = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/messaging-settings/company`,
        {
          company_name: config.company_name,
          company_phone: config.company_phone,
          service_area: config.service_area
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success('Company settings saved!');
    } catch (error) {
      toast.error('Failed to save company settings');
    } finally {
      setSaving(false);
    }
  };

  const saveSMSSettings = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/messaging-settings/sms`,
        config.sms,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success('SMS settings saved!');
    } catch (error) {
      toast.error('Failed to save SMS settings');
    } finally {
      setSaving(false);
    }
  };

  const saveEmailSettings = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/messaging-settings/email`,
        config.email,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success('Email settings saved!');
    } catch (error) {
      toast.error('Failed to save email settings');
    } finally {
      setSaving(false);
    }
  };

  const previewSMSTemplate = async (templateName) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/messaging-settings/templates/sms/preview`,
        { template: templateName },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setPreviewTemplate(response.data);
    } catch (error) {
      toast.error('Failed to load template preview');
    }
  };

  const updateConfig = (section, field, value) => {
    if (section) {
      setConfig(prev => ({
        ...prev,
        [section]: { ...prev[section], [field]: value }
      }));
    } else {
      setConfig(prev => ({ ...prev, [field]: value }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900">Messaging Settings</h1>
        <p className="text-slate-600 mt-1">
          Configure how your company communicates with leads and customers
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b">
        {[
          { id: 'company', label: 'Company Info', icon: Building },
          { id: 'sms', label: 'SMS Settings', icon: MessageSquare },
          { id: 'email', label: 'Email Settings', icon: Mail }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 font-medium transition-colors border-b-2 -mb-[2px] ${
              activeTab === tab.id
                ? 'text-orange-600 border-orange-500'
                : 'text-slate-500 border-transparent hover:text-slate-700'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Company Info Tab */}
      {activeTab === 'company' && (
        <div className="bg-white rounded-xl border p-6 space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="company_name">Company Name</Label>
              <div className="relative mt-1">
                <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="company_name"
                  value={config.company_name}
                  onChange={(e) => updateConfig(null, 'company_name', e.target.value)}
                  placeholder="Triple R Exteriors"
                  className="pl-10"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">
                This appears in all customer communications
              </p>
            </div>

            <div>
              <Label htmlFor="company_phone">Company Phone</Label>
              <div className="relative mt-1">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="company_phone"
                  type="tel"
                  value={config.company_phone || ''}
                  onChange={(e) => updateConfig(null, 'company_phone', e.target.value)}
                  placeholder="(984) 217-6631"
                  className="pl-10"
                />
              </div>
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="service_area">Service Area</Label>
              <div className="relative mt-1">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="service_area"
                  value={config.service_area || ''}
                  onChange={(e) => updateConfig(null, 'service_area', e.target.value)}
                  placeholder="NC Triangle Area"
                  className="pl-10"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Used in intro messages (e.g., "We specialize in roofing in the [Service Area]")
              </p>
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t">
            <Button onClick={saveCompanySettings} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Company Info
            </Button>
          </div>
        </div>
      )}

      {/* SMS Settings Tab */}
      {activeTab === 'sms' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-900">SMS Automation</h3>
                <p className="text-sm text-slate-500">Configure automated text messages to leads</p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.sms.enabled}
                  onChange={(e) => updateConfig('sms', 'enabled', e.target.checked)}
                  className="rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                />
                <span className="text-sm font-medium">Enabled</span>
              </label>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="twilio_phone_number">Your Twilio Phone Number</Label>
                <Input
                  id="twilio_phone_number"
                  type="tel"
                  value={config.sms.twilio_phone_number || ''}
                  onChange={(e) => updateConfig('sms', 'twilio_phone_number', e.target.value)}
                  placeholder="(984) 217-6631"
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Your dedicated Twilio number. Leave blank to use platform default.
                </p>
              </div>

              <div>
                <Label htmlFor="sms_sender_name">Sender Name</Label>
                <Input
                  id="sms_sender_name"
                  value={config.sms.sender_name}
                  onChange={(e) => updateConfig('sms', 'sender_name', e.target.value)}
                  placeholder="Chris"
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Signs off messages (e.g., "Chris, Triple R Exteriors")
                </p>
              </div>

              <div>
                <Label htmlFor="followup_delay">Follow-up Delay (minutes)</Label>
                <Input
                  id="followup_delay"
                  type="number"
                  min="1"
                  max="60"
                  value={config.sms.followup_delay_minutes}
                  onChange={(e) => updateConfig('sms', 'followup_delay_minutes', parseInt(e.target.value))}
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Time before sending inspection follow-up
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="font-medium text-slate-700">Auto-Response Features</h4>
              
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.sms.auto_respond_enabled}
                  onChange={(e) => updateConfig('sms', 'auto_respond_enabled', e.target.checked)}
                  className="rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                />
                <div>
                  <span className="text-sm font-medium text-slate-700">Auto-respond to incoming leads</span>
                  <p className="text-xs text-slate-500">Send intro message when a lead texts in</p>
                </div>
              </label>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.sms.estimate_auto_send}
                  onChange={(e) => updateConfig('sms', 'estimate_auto_send', e.target.checked)}
                  className="rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                />
                <div>
                  <span className="text-sm font-medium text-slate-700">Auto-send instant estimates</span>
                  <p className="text-xs text-slate-500">Generate and send estimates when requested via SMS</p>
                </div>
              </label>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.sms.require_sms_consent}
                  onChange={(e) => updateConfig('sms', 'require_sms_consent', e.target.checked)}
                  className="rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                />
                <div>
                  <span className="text-sm font-medium text-slate-700">Require SMS consent</span>
                  <p className="text-xs text-slate-500">Only send to leads who have opted in</p>
                </div>
              </label>
            </div>

            <div className="flex justify-end pt-4 border-t">
              <Button onClick={saveSMSSettings} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save SMS Settings
              </Button>
            </div>
          </div>

          {/* Message Templates Preview */}
          <div className="bg-white rounded-xl border p-6">
            <h3 className="font-semibold text-slate-900 mb-4">Message Templates</h3>
            <p className="text-sm text-slate-500 mb-4">
              Preview how your automated messages will look to customers
            </p>

            <div className="flex flex-wrap gap-2 mb-4">
              {['intro', 'estimate_sent', 'followup', 'appointment_request'].map(template => (
                <Button
                  key={template}
                  variant="outline"
                  size="sm"
                  onClick={() => previewSMSTemplate(template)}
                  className="capitalize"
                >
                  <Eye className="w-3 h-3 mr-1" />
                  {template.replace('_', ' ')}
                </Button>
              ))}
            </div>

            {previewTemplate && (
              <div className="bg-slate-50 rounded-lg p-4 border">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-slate-700 capitalize">
                    {previewTemplate.template_name.replace('_', ' ')} Template
                  </span>
                  <span className="text-xs text-slate-500">
                    {previewTemplate.character_count} characters
                  </span>
                </div>
                <pre className="whitespace-pre-wrap text-sm text-slate-600 font-sans">
                  {previewTemplate.preview}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Email Settings Tab */}
      {activeTab === 'email' && (
        <div className="bg-white rounded-xl border p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-slate-900">Email Communications</h3>
              <p className="text-sm text-slate-500">Configure automated emails to customers</p>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={config.email.enabled}
                onChange={(e) => updateConfig('email', 'enabled', e.target.checked)}
                className="rounded border-slate-300 text-orange-500 focus:ring-orange-500"
              />
              <span className="text-sm font-medium">Enabled</span>
            </label>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="email_sender_name">Sender Name</Label>
              <Input
                id="email_sender_name"
                value={config.email.sender_name}
                onChange={(e) => updateConfig('email', 'sender_name', e.target.value)}
                placeholder="Chris from Triple R Exteriors"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="reply_to_email">Reply-To Email</Label>
              <Input
                id="reply_to_email"
                type="email"
                value={config.email.reply_to_email || ''}
                onChange={(e) => updateConfig('email', 'reply_to_email', e.target.value)}
                placeholder="chris@triplerexteriors.com"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="primary_color">Brand Color</Label>
              <div className="flex gap-2 mt-1">
                <input
                  type="color"
                  value={config.email.primary_color}
                  onChange={(e) => updateConfig('email', 'primary_color', e.target.value)}
                  className="w-10 h-10 rounded border cursor-pointer"
                />
                <Input
                  id="primary_color"
                  value={config.email.primary_color}
                  onChange={(e) => updateConfig('email', 'primary_color', e.target.value)}
                  placeholder="#F97316"
                  className="font-mono"
                />
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="font-medium text-slate-700">Auto-Email Features</h4>
            
            {[
              { key: 'send_estimate_email', label: 'Send estimate emails', desc: 'Email estimates when generated' },
              { key: 'send_followup_emails', label: 'Send follow-up emails', desc: 'Automated follow-up sequences' },
              { key: 'send_review_requests', label: 'Send review requests', desc: 'Request reviews after job completion' },
              { key: 'send_referral_requests', label: 'Send referral requests', desc: 'Request referrals from happy customers' }
            ].map(item => (
              <label key={item.key} className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.email[item.key]}
                  onChange={(e) => updateConfig('email', item.key, e.target.checked)}
                  className="rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                />
                <div>
                  <span className="text-sm font-medium text-slate-700">{item.label}</span>
                  <p className="text-xs text-slate-500">{item.desc}</p>
                </div>
              </label>
            ))}
          </div>

          <div className="flex justify-end pt-4 border-t">
            <Button onClick={saveEmailSettings} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Email Settings
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessagingSettings;
